/**
 * Search Results Table — Add to Cart
 *
 * Uses event delegation on [data-search-table] to handle
 * "Add to Order" button clicks. Calls the Shopify Cart API
 * and dispatches a 'cart:update' event so the theme's header
 * cart count and cart drawer stay in sync.
 */

const CART_ADD_URL =
  (window.Theme && window.Theme.routes && window.Theme.routes.cart_add_url) ||
  '/cart/add.js';

const FEEDBACK_DURATION = 1500;

/**
 * Add a product variant to the cart.
 * @param {string} variantId
 * @param {number} quantity
 * @returns {Promise<object>}
 */
async function addToCart(variantId, quantity) {
  const body = JSON.stringify({
    items: [
      {
        id: parseInt(variantId, 10),
        quantity: quantity,
      },
    ],
  });

  const response = await fetch(CART_ADD_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.description || 'Could not add to cart');
  }

  return response.json();
}

/**
 * Show brief "Added!" feedback on the button, then revert.
 * @param {HTMLButtonElement} button
 */
function showFeedback(button) {
  const originalText = button.textContent.trim();
  button.textContent = 'Added!';
  button.classList.add('search-table__add-btn--added');
  button.disabled = true;

  setTimeout(() => {
    button.textContent = originalText;
    button.classList.remove('search-table__add-btn--added');
    button.disabled = false;
  }, FEEDBACK_DURATION);
}

/**
 * Dispatch the theme's cart:update event so other components react.
 * @param {HTMLElement} target
 * @param {string} variantId
 * @param {number} quantity
 */
function dispatchCartEvent(target, variantId, quantity) {
  const event = new CustomEvent('cart:update', {
    bubbles: true,
    detail: {
      sourceId: 'search-results-table',
      data: {
        source: 'search-results-table',
        itemCount: quantity,
        variantId: variantId,
      },
    },
  });
  target.dispatchEvent(event);
}

/**
 * Get the quantity value for a given row.
 * On desktop the row has a stepper; on mobile it defaults to 1.
 * @param {HTMLElement} row
 * @returns {number}
 */
function getQuantity(row) {
  const stepperValue = row.querySelector('[data-stepper-value]');
  if (stepperValue) {
    return parseInt(stepperValue.textContent, 10) || 1;
  }
  return 1;
}

/**
 * Initialize event delegation on all search tables.
 */
function init() {
  document.querySelectorAll('[data-search-table]').forEach((table) => {
    table.addEventListener('click', async (e) => {
      /* --- Stepper button handling --- */
      const stepperBtn = e.target.closest('[data-stepper-action]');
      if (stepperBtn) {
        const row = stepperBtn.closest('.search-table__row');
        if (!row) return;

        const valueEl = row.querySelector('[data-stepper-value]');
        if (!valueEl) return;

        const current = parseInt(valueEl.textContent, 10) || 1;
        const action = stepperBtn.dataset.stepperAction;
        let newValue = current;

        if (action === 'increase' && current < 99) {
          newValue = current + 1;
        } else if (action === 'decrease' && current > 1) {
          newValue = current - 1;
        }

        valueEl.textContent = newValue;

        const minusBtn = row.querySelector('[data-stepper-action="decrease"]');
        const plusBtn = row.querySelector('[data-stepper-action="increase"]');
        if (minusBtn) minusBtn.disabled = newValue <= 1;
        if (plusBtn) plusBtn.disabled = newValue >= 99;

        return;
      }

      /* --- Add to cart handling --- */
      const button = e.target.closest('.search-table__add-btn');
      if (!button || button.disabled) return;

      const variantId = button.dataset.variantId;
      if (!variantId) return;

      const row = button.closest('.search-table__row');
      const quantity = getQuantity(row);

      button.disabled = true;

      try {
        await addToCart(variantId, quantity);
        showFeedback(button);
        dispatchCartEvent(button, variantId, quantity);
      } catch (error) {
        console.error('[search-add-to-cart]', error.message);
        button.textContent = 'Error';
        button.disabled = false;
        setTimeout(() => {
          button.textContent = 'Add to PO';
        }, FEEDBACK_DURATION);
      }
    });
  });
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
